import React, { useState, useEffect } from 'react';
import { OperatingRoomSchedule, Employee, ManualAllocation, getEmployeeTypeDisplayName, OperatingRoom, Hospital, updateAllocations } from '../services/api.ts';
import { isCredentialedAtHospital } from './boardViewHelpers.ts';

interface ManualAllocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (allocation: ManualAllocation) => void;
  schedule: OperatingRoomSchedule | null;
  employees: Employee[];
  shifts: any[]; // We'll need to get shifts for the day
  selectedDate: string;
  operatingRooms: OperatingRoom[];
  hospitals: Hospital[];
  existingAllocation?: ManualAllocation; // For pre-populating during modifications
  allAllocations?: any[]; // All current allocations to filter out busy employees
  modifiedAllocations?: Map<string, ManualAllocation>; // Track modifications in memory
  startTimeUtc: string; // UTC start time for the day
  endTimeUtc: string; // UTC end time for the day
  onReloadAllocations: () => Promise<void>; // Callback to reload allocations from server
  isModifying?: boolean; // True when editing an already-allocated schedule (eager persist); false for pre-run pins (in-memory only)
}

const ManualAllocationModal: React.FC<ManualAllocationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  schedule,
  employees,
  shifts,
  selectedDate,
  operatingRooms,
  hospitals,
  existingAllocation,
  allAllocations = [],
  modifiedAllocations = new Map(),
  startTimeUtc,
  endTimeUtc,
  onReloadAllocations,
  isModifying = false
}) => {
  const [selectedNurses, setSelectedNurses] = useState<string[]>([]);
  const [selectedDoctors, setSelectedDoctors] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Helper functions to get operating room and hospital information
  const getOperatingRoomName = (operatingRoomId: string): string => {
    const room = operatingRooms.find(room => room.operatingRoomId === operatingRoomId);
    return room ? room.name : operatingRoomId;
  };

  const getOperatingRoomCredential = (operatingRoomId: string): string | null => {
    const room = operatingRooms.find(room => room.operatingRoomId === operatingRoomId);
    return room ? room.credential : null;
  };

  const getOperatingRoomHospitalId = (operatingRoomId: string): string | null => {
    const room = operatingRooms.find(room => room.operatingRoomId === operatingRoomId);
    return room ? room.hospitalId : null;
  };

  const getHospitalName = (hospitalId: string): string => {
    const hospital = hospitals.find(hosp => hosp.hospitalId === hospitalId);
    return hospital ? hospital.name : hospitalId;
  };

  // Hospital-level credentialing: a provider may only work at a hospital they're credentialed for. The rule
  // (incl. the no-credentials-on-file escape) is shared with BoardView so the picker can never offer someone the
  // board would then refuse to place — see isCredentialedAtHospital in boardViewHelpers.
  const isEmployeeCredentialedAtHospital = (employee: any, hospitalId: string): boolean =>
    isCredentialedAtHospital(employee?.credentials, hospitalId,
      c => hospitals.find(h => h.hospitalId === c || h.name === c)?.hospitalId);

  const formatTime = (utcTime: string): string => {
    return new Date(utcTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    if (isOpen && schedule) {
      // Pre-populate if existing allocation is provided (modification mode)
      if (existingAllocation) {
        setSelectedNurses(existingAllocation.allocatedNurseIds || []);
        setSelectedDoctors(existingAllocation.allocatedDoctorIds || []);
      } else {
        // Reset selections when modal opens
        setSelectedNurses([]);
        setSelectedDoctors([]);
      }
      setErrors([]);
    }
  }, [isOpen, schedule, existingAllocation]);

  if (!isOpen || !schedule) {
    return null;
  }


  // Helper function to check if an employee is already allocated to another overlapping schedule
  const isEmployeeAllocatedElsewhere = (employeeId: string): boolean => {
    if (!schedule) return false;
    
    // Check both persisted allocations and modified allocations
    for (const allocation of allAllocations) {
      // Skip the current schedule
      if (allocation.operatingRoomScheduleId === schedule.scheduleId) continue;
      
      // Check if this allocation has been modified
      const modified = modifiedAllocations.get(allocation.operatingRoomScheduleId);
      const allocatedNurses = modified?.allocatedNurseIds || allocation.allocatedNurseIds || [];
      const allocatedDoctors = modified?.allocatedDoctorIds || allocation.allocatedDoctorIds || [];
      
      // Check if employee is in this allocation
      if (![...allocatedNurses, ...allocatedDoctors].includes(employeeId)) continue;
      
      // Nurses/students can't float, so if they're allocated elsewhere, they're not available
      // (We could add overlap checking here in the future if needed)
      return true;
    }
    
    return false;
  };

  // Helper function to check if an employee is available for a schedule (same logic as allocation algorithm)
  const isEmployeeAvailableForSchedule = (employee: Employee, employeeShifts: any[]): boolean => {
    // Find the shift that fully contains the schedule time (not just overlaps)
    let validShift: any = null;
    for (const shift of employeeShifts) {
      const shiftStart = new Date(shift.startTimeUtc);
      const shiftEnd = new Date(shift.endTimeUtc);
      const scheduleStart = new Date(schedule.startTimeUtc);
      const scheduleEnd = new Date(schedule.endTimeUtc);
      // Check for full containment: shift starts at or before schedule start AND shift ends at or after schedule end
      if (shiftStart <= scheduleStart && shiftEnd >= scheduleEnd) {
        validShift = shift;
        break;
      }
    }
    
    if (!validShift) return false;
    
    // Check credentials if operating room requires them
    const operatingRoom = operatingRooms.find(room => room.operatingRoomId === schedule.operatingRoomId);
    if (operatingRoom?.credential && (!employee.credentials || !employee.credentials.includes(operatingRoom.credential))) {
      return false;
    }

    // Hospital-level credentialing: don't offer someone for a room at a hospital they can't work at.
    const roomHospitalId = getOperatingRoomHospitalId(schedule.operatingRoomId);
    if (roomHospitalId && !isEmployeeCredentialedAtHospital(employee, roomHospitalId)) {
      return false;
    }

    return true;
  };

  // Filter employees who have shifts on the selected date and are available for this schedule
  const availableNurses = employees.filter(emp => {
    if (emp.employeeType !== 'nurse' && emp.employeeType !== 'nurse-student-junior' && emp.employeeType !== 'nurse-student-senior') return false;
    
    // Allow if already selected (for modification mode)
    if (selectedNurses.includes(emp.employeeId)) return true;
    
    // Filter out if allocated elsewhere (unless they're being freed up)
    if (isEmployeeAllocatedElsewhere(emp.employeeId)) return false;
    
    // Get employee's shifts for the day
    const employeeShifts = shifts.filter(shift => shift.employeeId === emp.employeeId);
    if (employeeShifts.length === 0) return false;
    
    // Use the same validation logic as the allocation algorithm
    return isEmployeeAvailableForSchedule(emp, employeeShifts);
  });

  const availableDoctors = employees.filter(emp => {
    if (emp.employeeType !== 'doctor') return false;
    
    // Allow if already selected (for modification mode)
    if (selectedDoctors.includes(emp.employeeId)) return true;
    
    // Doctors can float, so we don't filter them out based on other allocations
    // The backend will handle floating limits
    
    // Get employee's shifts for the day
    const employeeShifts = shifts.filter(shift => shift.employeeId === emp.employeeId);
    if (employeeShifts.length === 0) return false;
    
    // Use the same validation logic as the allocation algorithm
    return isEmployeeAvailableForSchedule(emp, employeeShifts);
  });

  // Helper function to get employees who have shifts but aren't available (for informational display)
  const getUnavailableEmployeesWithReasons = () => {
    const unavailable: { employee: Employee; reason: string }[] = [];
    
    // Check nurses
    const nursesWithShifts = employees.filter(emp => 
      (emp.employeeType === 'nurse' || emp.employeeType === 'nurse-student-junior' || emp.employeeType === 'nurse-student-senior') &&
      shifts.some(shift => shift.employeeId === emp.employeeId)
    );
    
    for (const nurse of nursesWithShifts) {
      if (!availableNurses.includes(nurse)) {
        const operatingRoom = operatingRooms.find(room => room.operatingRoomId === schedule.operatingRoomId);
        
        // Check if it's a credential issue
        if (operatingRoom?.credential && (!nurse.credentials || !nurse.credentials.includes(operatingRoom.credential))) {
          unavailable.push({ employee: nurse, reason: `Missing required credential: ${operatingRoom.credential}` });
        } else {
          // Must be a shift coverage issue
          unavailable.push({ employee: nurse, reason: 'Shift does not fully cover the schedule time' });
        }
      }
    }
    
    // Check doctors
    const doctorsWithShifts = employees.filter(emp => 
      emp.employeeType === 'doctor' &&
      shifts.some(shift => shift.employeeId === emp.employeeId)
    );
    
    for (const doctor of doctorsWithShifts) {
      if (!availableDoctors.includes(doctor)) {
        const operatingRoom = operatingRooms.find(room => room.operatingRoomId === schedule.operatingRoomId);
        
        // Check if it's a credential issue
        if (operatingRoom?.credential && (!doctor.credentials || !doctor.credentials.includes(operatingRoom.credential))) {
          unavailable.push({ employee: doctor, reason: `Missing required credential: ${operatingRoom.credential}` });
        } else {
          // Must be a shift coverage issue
          unavailable.push({ employee: doctor, reason: 'Shift does not fully cover the schedule time' });
        }
      }
    }
    
    return unavailable;
  };

  const handleNurseChange = (employeeId: string, checked: boolean) => {
    if (checked) {
      setSelectedNurses([...selectedNurses, employeeId]);
    } else {
      setSelectedNurses(selectedNurses.filter(id => id !== employeeId));
    }
  };

  const handleDoctorChange = (employeeId: string, checked: boolean) => {
    if (checked) {
      setSelectedDoctors([...selectedDoctors, employeeId]);
    } else {
      setSelectedDoctors(selectedDoctors.filter(id => id !== employeeId));
    }
  };

  const handleSave = async () => {
    const validationErrors: string[] = [];

    // Validate that all selected employees are actually available
    const allSelectedEmployees = [...selectedNurses, ...selectedDoctors];
    for (const employeeId of allSelectedEmployees) {
      const employee = employees.find(emp => emp.employeeId === employeeId);
      if (!employee) {
        validationErrors.push(`Employee ${employeeId} not found`);
        continue;
      }

      const employeeShifts = shifts.filter(shift => shift.employeeId === employeeId);
      if (!isEmployeeAvailableForSchedule(employee, employeeShifts)) {
        validationErrors.push(`${employee.firstName} ${employee.lastName} is not available for this schedule (shift doesn't cover the full schedule time or missing required credentials)`);
      }
    }

    // An allocation must pin at least one doctor OR at least one nurse. A nurse
    // without a doctor is allowed here (the algorithm/validation surfaces
    // MISSING_DOCTOR later) — we only reject a completely empty save.
    if (selectedNurses.length === 0 && selectedDoctors.length === 0) {
      validationErrors.push('Select at least one doctor or nurse before saving.');
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    const manualAllocation: ManualAllocation = {
      scheduleId: schedule.scheduleId,
      allocatedNurseIds: selectedNurses,
      allocatedDoctorIds: selectedDoctors
    };

    // Pre-run pins (not modifying): hold the allocation in BoardView's in-memory
    // state via onSave. Do NOT persist to the server — these pins are fed to the
    // allocation algorithm when the user runs it, not written as final allocations.
    if (!isModifying) {
      onSave(manualAllocation);
      onClose();
      return;
    }

    // Modify mode: an allocation already exists on the server. Persist the edit
    // immediately and reload the authoritative state.
    setIsSaving(true);
    try {
      // Collect all allocations including this one
      const allAllocationsToSave: Array<{scheduleId: string, allocatedNurseIds: string[], allocatedDoctorIds: string[]}> = [];

      // Add all existing modified allocations (if any)
      modifiedAllocations.forEach((alloc) => {
        if (alloc.scheduleId !== schedule.scheduleId) {
          allAllocationsToSave.push(alloc);
        }
      });

      // Add all unmodified allocations from current view
      if (allAllocations) {
        allAllocations.forEach((alloc: any) => {
          // Skip if this schedule was modified
          if (!modifiedAllocations.has(alloc.operatingRoomScheduleId) && alloc.operatingRoomScheduleId !== schedule.scheduleId) {
            allAllocationsToSave.push({
              scheduleId: alloc.operatingRoomScheduleId,
              allocatedNurseIds: alloc.allocatedNurseIds || [],
              allocatedDoctorIds: alloc.allocatedDoctorIds || []
            });
          }
        });
      }

      // Add current allocation
      allAllocationsToSave.push(manualAllocation);

      // Call API to save all allocations
      await updateAllocations(allAllocationsToSave, startTimeUtc, endTimeUtc);

      // Reload fresh allocations from server
      await onReloadAllocations();

      onClose();
    } catch (error) {
      console.error('Error saving allocations:', error);
      setErrors(['Failed to save allocations. Please try again.']);
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Manual Staff Allocation</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="schedule-details">
            <h3>{schedule.surgeryType}</h3>
            <div className="time-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
              <strong>Time:</strong> {formatTime(schedule.startTimeUtc)} – {formatTime(schedule.endTimeUtc)}
            </div>
            <div className="date-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
              <strong>Date:</strong> {new Date(schedule.startTimeUtc).toLocaleDateString()}
            </div>
            <div className="room-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
              <strong>Operating Room:</strong> {getOperatingRoomName(schedule.operatingRoomId)}
            </div>
            {(() => {
              const hospitalId = getOperatingRoomHospitalId(schedule.operatingRoomId);
              return hospitalId ? (
                <div className="hospital-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
                  <strong>Hospital:</strong> {getHospitalName(hospitalId)}
                </div>
              ) : null;
            })()}
            {(() => {
              const credential = getOperatingRoomCredential(schedule.operatingRoomId);
              return credential ? (
                <div className="credential-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
                  <strong>Required Credential:</strong> {credential}
                </div>
              ) : (
                <div className="credential-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
                  <strong>Required Credential:</strong> <em>None</em>
                </div>
              );
            })()}
          </div>

          {errors.length > 0 && (
            <div className="alert alert-error">
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="allocation-section">
            <h4>Select CRNAs ({selectedNurses.length})</h4>
            <div className="employee-checkboxes">
              {availableNurses.map(nurse => (
                <label key={nurse.employeeId} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedNurses.includes(nurse.employeeId)}
                    onChange={(e) => handleNurseChange(nurse.employeeId, e.target.checked)}
                  />
                  {nurse.firstName} {nurse.lastName} ({getEmployeeTypeDisplayName(nurse.employeeType)})
                </label>
              ))}
              {availableNurses.length === 0 && (
                <p className="text-muted">No CRNAs available for this time slot.</p>
              )}
            </div>
          </div>

          <div className="allocation-section">
            <h4>Select Doctors ({selectedDoctors.length})</h4>
            <div className="employee-checkboxes">
              {availableDoctors.map(doctor => (
                <label key={doctor.employeeId} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedDoctors.includes(doctor.employeeId)}
                    onChange={(e) => handleDoctorChange(doctor.employeeId, e.target.checked)}
                  />
                  {doctor.firstName} {doctor.lastName} ({getEmployeeTypeDisplayName(doctor.employeeType)})
                </label>
              ))}
              {availableDoctors.length === 0 && (
                <p className="text-muted">No doctors available for this time slot.</p>
              )}
            </div>
          </div>

          {/* Show employees who have shifts but aren't available */}
          {(() => {
            const unavailableEmployees = getUnavailableEmployeesWithReasons();
            if (unavailableEmployees.length > 0) {
              return (
                <div className="allocation-section">
                  <h4>Employees with Shifts (Not Available)</h4>
                  <div className="unavailable-employees" style={{ fontSize: '0.9em', color: 'var(--text-muted)' }}>
                    {unavailableEmployees.map((item, index) => (
                      <div key={index} style={{ marginBottom: '4px' }}>
                        <strong>{item.employee.firstName} {item.employee.lastName}</strong> ({getEmployeeTypeDisplayName(item.employee.employeeType)}) - {item.reason}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Allocation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualAllocationModal;
